import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, RadialGradient, Rect, Stop } from 'react-native-svg';

import { IconRemoveSvg, MilestoneCurrentSvg } from '../../../assets/achievements';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import AchievementIndicator from './AchievementIndicator';
import {
	ACHIEVEMENTS_FONT,
	achievementsColors,
	achievementsGlow,
	GREEN_BUTTON_FILL,
	PROGRESS_FILL,
	RADIUS_CARD,
} from './achievementsTheme';
import {
	achievementText,
	AchievementView,
	formatAchievementValue,
	tierBadge,
	tierRequirement,
} from './achievementsViewModel';

/**
 * Figma "Modal / Achievement Details" (4488:19797, and 4488:19983 for the on-profile state) - the
 * body under the shared header: the hero badge, the rail, the progress panel and the
 * add/remove action.
 *
 * The rail's geometry is the file's: the earned half of the
 * track in gold/500 and the rest in the locked grey, names at 82 and requirements at 118. The file
 * draws the badges a couple of points apart vertically (-12, -12, -10, -14, -14), which is jitter
 * from the artwork rather than a rhythm, so they sit on one line here.
 *
 * A locked achievement has no frame in "01 · Entry point": it keeps the same rail with nothing
 * earned, and no action, since only an unlocked achievement can go on the profile.
 */
const HERO = { size: 150, top: 20, bloom: 36 };
/** The hero's gold bloom (`0 0 24 rgba(255,156,0,0.55)` on the hexagon). */
const HERO_BLOOM = { color: '#FF9C00', opacity: 0.55 };
const TITLE_TOP = 170;
const DESCRIPTION_TOP = 200;
const CONTENT_X = 12;
const CONTENT_WIDTH = 334;

const RAIL = {
	top: 250,
	height: 148,
	firstNode: 29,
	trackY: 65,
	badge: 62,
	badgeTop: -12,
	nameTop: 82,
	requirementTop: 118,
	captionWidth: 66,
	markerSize: 20,
	currentSize: 30,
};

const PROGRESS = {
	top: 412,
	height: 112,
	inset: 15,
	headingTop: 13,
	trackTop: 43,
	trackWidth: 302,
	trackHeight: 14,
	valueTop: 67,
	requirementTop: 81,
};

const BUTTON = { top: 542, height: 48, iconSize: 18, gap: 10 };

/**
 * A locked achievement has no action, so its panel stops under the progress panel instead of
 * keeping an empty band where the button would be.
 */
export const DETAILS_NO_ACTION_TRIM = BUTTON.top + BUTTON.height - (PROGRESS.top + PROGRESS.height);

type Props = {
	achievement: AchievementView;
	selected: boolean;
	onToggleProfile: () => void;
	scale: number;
};

export const AchievementDetails = ({ achievement, selected, onToggleProfile, scale }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const greenFillId = useSvgId('achievementAddButton');
	const progressFillId = useSvgId('achievementProgress');
	const heroBloomId = useSvgId('achievementHeroBloom');
	const { t } = useTranslation();

	const { locked, tiers } = achievement;
	const progressTier =
		achievement.nextTier ?? (achievement.allTiersDone ? null : achievement.currentTier);

	const nodeGap = tiers.length > 1 ? (CONTENT_WIDTH - RAIL.firstNode * 2) / (tiers.length - 1) : 0;
	const nodeCenter = (index: number) =>
		tiers.length > 1 ? RAIL.firstNode + index * nodeGap : CONTENT_WIDTH / 2;
	// The file runs the gold half of the track up to the node the player is working towards, and
	// leaves the rest in the locked grey.
	const workingIndex = achievement.nextTier
		? tiers.findIndex(tier => tier.key === achievement.nextTier?.key)
		: tiers.length - 1;
	const earnedTrackEnd = nodeCenter(locked ? 0 : Math.max(0, workingIndex));
	const railEnd = nodeCenter(tiers.length - 1);

	const progressRatio = progressTier
		? Math.max(0, Math.min(1, achievement.progress / progressTier.target))
		: 1;

	return (
		<View style={styles.content}>
			{/* A shadow on the square hero box drew a visible square on web and Android, so the
			    bloom is a radial fade behind the hexagon instead - and only for earned art. */}
			{!locked && (
				<View pointerEvents='none' style={styles.heroBloom}>
					<Svg width='100%' height='100%'>
						<Defs>
							<RadialGradient id={heroBloomId} cx='50%' cy='50%' r='50%'>
								<Stop offset='0.45' stopColor={HERO_BLOOM.color} stopOpacity={HERO_BLOOM.opacity} />
								<Stop offset='1' stopColor={HERO_BLOOM.color} stopOpacity={0} />
							</RadialGradient>
						</Defs>
						<Circle cx='50%' cy='50%' r='50%' fill={`url(#${heroBloomId})`} />
					</Svg>
				</View>
			)}
			<View style={styles.hero}>{achievement.renderBadge(HERO.size * scale)}</View>

			<Text numberOfLines={1} style={styles.title}>
				{t(achievement.nameKey)}
			</Text>
			<Text style={styles.description}>{t(achievement.taglineKey)}</Text>

			<View style={styles.rail}>
				<View
					style={[
						styles.track,
						{
							left: nodeCenter(0) * scale,
							width: Math.max(0, earnedTrackEnd - nodeCenter(0)) * scale,
							backgroundColor: achievementsColors.gold500,
						},
					]}
				/>
				<View
					style={[
						styles.track,
						{
							left: earnedTrackEnd * scale,
							width: Math.max(0, railEnd - earnedTrackEnd) * scale,
							backgroundColor: achievementsColors.lockedBorder,
						},
					]}
				/>

				{tiers.map((tier, index) => {
					const center = nodeCenter(index);
					const tierEarned = tier.unlocked;

					return (
						<React.Fragment key={tier.key}>
							<View style={[styles.railBadge, { left: (center - RAIL.badge / 2) * scale }]}>
								{tierBadge(tier)(RAIL.badge * scale)}
							</View>
							<Text
								numberOfLines={2}
								style={[
									styles.tierName,
									{ left: (center - RAIL.captionWidth / 2) * scale },
									tierEarned ? styles.tierNameEarned : styles.tierNameLocked,
								]}
							>
								{t(tier.nameKey)}
							</Text>
							<Text
								numberOfLines={2}
								style={[
									styles.tierRequirement,
									{ left: (center - RAIL.captionWidth / 2) * scale },
									tierEarned ? styles.tierRequirementEarned : styles.tierNameLocked,
								]}
							>
								{tierRequirement(t, tier)}
							</Text>

							{tierEarned ? (
								// "Milestone / Current" (4488:19830): a 14 gold dot inside a 30 box of glow.
								<View
									pointerEvents='none'
									style={[
										styles.milestone,
										{
											left: (center - RAIL.currentSize / 2) * scale,
											top: (RAIL.trackY + 1 - RAIL.currentSize / 2) * scale,
										},
									]}
								>
									<MilestoneCurrentSvg
										width={RAIL.currentSize * scale}
										height={RAIL.currentSize * scale}
									/>
								</View>
							) : (
								<AchievementIndicator
									scale={scale}
									state='locked'
									size={RAIL.markerSize}
									style={{
										position: 'absolute',
										left: (center - RAIL.markerSize / 2) * scale,
										top: (RAIL.trackY + 1 - RAIL.markerSize / 2) * scale,
									}}
								/>
							)}
						</React.Fragment>
					);
				})}
			</View>

			<View style={styles.progress}>
				<Text numberOfLines={1} style={styles.progressHeading}>
					{progressTier
						? t(progressTier.nameKey)
						: achievementText(t, 'T_SITE_ACHIEVEMENTS_ALL_TIERS_DONE')}
				</Text>

				<View style={styles.progressTrack} />
				<View
					style={[styles.progressFillClip, { width: PROGRESS.trackWidth * progressRatio * scale }]}
				>
					<Svg width={PROGRESS.trackWidth * scale} height={PROGRESS.trackHeight * scale}>
						<Defs>
							<LinearGradient id={progressFillId} x1='0' y1='0' x2='0' y2='1'>
								{PROGRESS_FILL.map(([offset, color]) => (
									<Stop key={offset} offset={offset} stopColor={color} />
								))}
							</LinearGradient>
						</Defs>
						<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${progressFillId})`} />
					</Svg>
				</View>

				<Text numberOfLines={1} style={styles.progressFrom}>
					{formatAchievementValue(achievement.progress)}
				</Text>
				{progressTier && (
					<>
						<Text numberOfLines={1} style={styles.progressTo}>
							{formatAchievementValue(progressTier.target)}
						</Text>
						<Text numberOfLines={1} style={styles.progressRequirement}>
							{tierRequirement(t, progressTier)}
						</Text>
					</>
				)}
			</View>

			{!locked &&
				(selected ? (
					<Pressable
						accessibilityRole='button'
						onPress={onToggleProfile}
						style={[styles.button, styles.removeButton]}
					>
						<View>
							<IconRemoveSvg width={BUTTON.iconSize * scale} height={BUTTON.iconSize * scale} />
						</View>
						<Text numberOfLines={1} style={styles.removeLabel}>
							{achievementText(t, 'T_SITE_ACHIEVEMENTS_REMOVE_FROM_PROFILE')}
						</Text>
					</Pressable>
				) : (
					<Pressable
						accessibilityRole='button'
						onPress={onToggleProfile}
						style={[styles.button, styles.addButton]}
					>
						<View pointerEvents='none' style={styles.buttonFill}>
							<Svg width='100%' height='100%'>
								<Defs>
									<LinearGradient id={greenFillId} x1='0' y1='0' x2='0' y2='1'>
										{GREEN_BUTTON_FILL.map(([offset, color]) => (
											<Stop key={offset} offset={offset} stopColor={color} />
										))}
									</LinearGradient>
								</Defs>
								<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${greenFillId})`} />
							</Svg>
						</View>
						<Text numberOfLines={1} style={styles.addLabel}>
							{achievementText(t, 'T_SITE_ACHIEVEMENTS_ADD_TO_PROFILE')}
						</Text>
					</Pressable>
				))}
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		content: {
			flex: 1,
		},
		// "Achievement badge / Hero" (4488:19845): the 150 square badge; its bloom is `heroBloom`.
		hero: {
			position: 'absolute',
			left: ((CONTENT_WIDTH + CONTENT_X * 2 - HERO.size) / 2) * scale,
			top: HERO.top * scale,
			width: HERO.size * scale,
			height: HERO.size * scale,
		},
		heroBloom: {
			position: 'absolute',
			left: ((CONTENT_WIDTH + CONTENT_X * 2 - HERO.size) / 2 - HERO.bloom) * scale,
			top: (HERO.top - HERO.bloom) * scale,
			width: (HERO.size + HERO.bloom * 2) * scale,
			height: (HERO.size + HERO.bloom * 2) * scale,
		},
		// Sofia Sans Bold 20, line-height 1.2, gold/100.
		title: {
			position: 'absolute',
			left: CONTENT_X * scale,
			top: TITLE_TOP * scale,
			width: CONTENT_WIDTH * scale,
			color: achievementsColors.gold100,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 20 * scale,
			fontWeight: '700',
			lineHeight: 20 * 1.2 * scale,
			textAlign: 'center',
		},
		// Sofia Sans SemiBold 12, line-height 1.4, gold/50.
		description: {
			position: 'absolute',
			left: CONTENT_X * scale,
			top: DESCRIPTION_TOP * scale,
			width: CONTENT_WIDTH * scale,
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 12 * 1.4 * scale,
			textAlign: 'center',
		},
		rail: {
			position: 'absolute',
			left: CONTENT_X * scale,
			top: RAIL.top * scale,
			width: CONTENT_WIDTH * scale,
			height: RAIL.height * scale,
		},
		track: {
			position: 'absolute',
			top: RAIL.trackY * scale,
			height: 2 * scale,
			borderRadius: 1 * scale,
		},
		railBadge: {
			position: 'absolute',
			top: RAIL.badgeTop * scale,
			width: RAIL.badge * scale,
			height: RAIL.badge * scale,
		},
		// Sofia Sans SemiBold 12 / 10, both line-height 1.2, centred on their node.
		tierName: {
			position: 'absolute',
			top: RAIL.nameTop * scale,
			width: RAIL.captionWidth * scale,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 12 * 1.2 * scale,
			textAlign: 'center',
		},
		tierRequirement: {
			position: 'absolute',
			top: RAIL.requirementTop * scale,
			width: RAIL.captionWidth * scale,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 10 * scale,
			fontWeight: '600',
			lineHeight: 10 * 1.2 * scale,
			textAlign: 'center',
		},
		tierNameEarned: {
			color: achievementsColors.gold100,
		},
		tierRequirementEarned: {
			color: achievementsColors.gold50,
		},
		tierNameLocked: {
			color: achievementsColors.secondary,
		},
		milestone: {
			position: 'absolute',
		},
		progress: {
			position: 'absolute',
			left: CONTENT_X * scale,
			top: PROGRESS.top * scale,
			width: CONTENT_WIDTH * scale,
			height: PROGRESS.height * scale,
			borderRadius: 12 * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.panelOrange,
			backgroundColor: achievementsColors.surface,
		},
		progressHeading: {
			position: 'absolute',
			left: PROGRESS.inset * scale,
			top: PROGRESS.headingTop * scale,
			width: 200 * scale,
			color: achievementsColors.muted,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 12 * 1.2 * scale,
		},
		progressTrack: {
			position: 'absolute',
			left: PROGRESS.inset * scale,
			top: PROGRESS.trackTop * scale,
			width: PROGRESS.trackWidth * scale,
			height: PROGRESS.trackHeight * scale,
			borderRadius: (PROGRESS.trackHeight / 2) * scale,
			backgroundColor: achievementsColors.overlay,
		},
		// The gradient is drawn at full width and revealed by the clip, so its ramp does not
		// stretch or squash with the value.
		progressFillClip: {
			position: 'absolute',
			left: PROGRESS.inset * scale,
			top: PROGRESS.trackTop * scale,
			height: PROGRESS.trackHeight * scale,
			borderRadius: (PROGRESS.trackHeight / 2) * scale,
			overflow: 'hidden',
		},
		progressFrom: {
			position: 'absolute',
			left: PROGRESS.inset * scale,
			top: PROGRESS.valueTop * scale,
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '700',
			lineHeight: 12 * 1.2 * scale,
		},
		progressTo: {
			position: 'absolute',
			right: PROGRESS.inset * scale,
			top: PROGRESS.valueTop * scale,
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '700',
			lineHeight: 12 * 1.2 * scale,
			textAlign: 'right',
		},
		progressRequirement: {
			position: 'absolute',
			right: PROGRESS.inset * scale,
			top: PROGRESS.requirementTop * scale,
			color: achievementsColors.secondary,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 10 * scale,
			fontWeight: '600',
			lineHeight: 10 * 1.2 * scale,
			textAlign: 'right',
		},
		button: {
			position: 'absolute',
			left: CONTENT_X * scale,
			top: BUTTON.top * scale,
			width: CONTENT_WIDTH * scale,
			height: BUTTON.height * scale,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: BUTTON.gap * scale,
			borderRadius: RADIUS_CARD * scale,
			borderWidth: Math.max(1, 1 * scale),
		},
		addButton: {
			borderColor: achievementsColors.greenStroke,
			overflow: 'hidden',
			...achievementsGlow.green,
		},
		buttonFill: {
			...StyleSheet.absoluteFillObject,
		},
		// "Label/Button": Sofia Sans Black 18, line-height 1.3, letter-spacing 0.36, uppercase.
		addLabel: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 18 * scale,
			fontWeight: '900',
			lineHeight: 18 * 1.3 * scale,
			letterSpacing: 0.36 * scale,
		},
		removeButton: {
			backgroundColor: achievementsColors.well,
			borderColor: achievementsColors.gold50,
			...achievementsGlow.gold,
		},
		// Sofia Sans Black 16, line-height 1.2, uppercase.
		removeLabel: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 16 * scale,
			fontWeight: '900',
			lineHeight: 16 * 1.2 * scale,
		},
	});

export default AchievementDetails;
